// Charges (shared/charges.ts, migration 050): what a maker charges to build something that isn't a part with stock. The
// table both places edit: a BOM's Charges tab (the plan, in USD; build_charge.set) and a make PO build's charges for
// that order (in the PO's currency; po_line.set_charges, which prices the line). Each change saves the whole list,
// shown at once (so a second quick change starts from the first). The usual table (DataTable: views, columns, Copy / CSV).
import { Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { familyKey } from "../../../shared/boms.ts";
import { calc } from "../../../shared/calc.ts";
import {
  CHARGE_LAYER_LABELS,
  CHARGE_LAYERS,
  CHARGE_PER_LABELS,
  CHARGE_PERS,
  CHARGE_SUGGESTIONS,
  type ChargeLayer,
  type ChargePer,
  chargesTotal,
  linePrice,
} from "../../../shared/charges.ts";
import { type Bom, DEFAULTS, type Po, type PoLine } from "../../../shared/db.gen.ts";
import { linesEditable } from "../../../shared/po.ts";
import { money, plural } from "../lib/format.ts";
import { can, mutateWithResult } from "../lib/mutate.ts";
import { type Change, useRows } from "../lib/sync.ts";
import { type Column, DataTable } from "../ui/DataTable.tsx";

export type ChargeRow = { id: string; name: string; amount: number | null; per: ChargePer; layer: ChargeLayer };

const toRow = (r: { id: string; name: string; amount: unknown; per: ChargePer; layer: ChargeLayer }): ChargeRow => ({
  id: r.id,
  name: r.name,
  amount: r.amount == null || r.amount === "" ? null : Number(r.amount),
  per: r.per,
  layer: r.layer,
});

/** 0.3 → "0.30", 0.0985 → "0.0985": cents always, more places only when it has them. */
const amountText = (n: number) => ((String(n).split(".")[1] ?? "").length < 2 ? n.toFixed(2) : String(n));

/** "12.5" or a sum ("40/500") → the amount (to 4 places), "" → null, anything else → undefined (not a number). */
const parseAmount = (text: string) => {
  if (!text.trim()) return null;
  const n = calc(text, 4);
  return n != null && n >= 0 ? n : undefined;
};

const PER_OPTIONS = CHARGE_PERS.map((p) => ({ value: p, label: CHARGE_PER_LABELS[p] }));
const LAYER_OPTIONS = CHARGE_LAYERS.map((l) => ({ value: l, label: CHARGE_LAYER_LABELS[l] }));

/** The list as it will be stored, to show at once: rows kept or added in order, the others gone. */
function shown(table: "build_charge" | "po_line_charge", before: readonly { id: string }[], next: ChargeRow[], owner: Record<string, unknown>): Change[] {
  const now = new Date().toISOString();
  const defaults = Object.fromEntries(Object.entries(DEFAULTS[table]).map(([k, v]) => [k, v === "now" ? now : v]));
  const changes: Change[] = next.map((r, position) => ({
    t: table,
    id: r.id,
    row: { ...defaults, ...(before.find((b) => b.id === r.id) ?? {}), ...owner, ...r, position, updated_at: now },
  }));
  for (const b of before) if (!next.some((r) => r.id === b.id)) changes.push({ t: table, id: b.id, row: null });
  return changes;
}

/**
 * The charges as rows to edit (the usual table: click a cell to change it, Enter saves and moves down), with Add charge
 * (the usual names suggested) and their total per unit in the totals row. `exportName` and `viewsSlot`: the table's
 * name and where its View and Columns menus go (by the title of what it's in).
 */
export function ChargeTable({
  rows,
  currency,
  editable,
  amountRequired,
  qty,
  onSave,
  exportName,
  viewsSlot,
}: {
  rows: ChargeRow[];
  currency: string;
  editable: boolean;
  /** A make PO line's charges always have an amount. */
  amountRequired?: boolean;
  /** The order's units, so per-order charges show what they add per unit. */
  qty?: number;
  onSave: (rows: ChargeRow[]) => void;
  exportName: string;
  viewsSlot: HTMLElement | null;
}) {
  const save = (next: ChargeRow[]) => onSave(next);
  // By id, not place: the table can be sorted, and the list keeps its own order.
  const change = (id: string, patch: Partial<ChargeRow>) => save(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const add = (s: { name: string; layer: ChargeLayer; per: ChargePer }) =>
    save([...rows, { id: crypto.randomUUID(), name: s.name, layer: s.layer, per: s.per, amount: amountRequired ? 0 : null }]);
  const used = new Set(rows.map((r) => r.name.toLowerCase()));
  const total = chargesTotal(rows, qty);
  const position = new Map(rows.map((r, i) => [r.id, i + 1]));
  const columns: Column<ChargeRow>[] = [
    {
      key: "name",
      header: "Charge",
      width: "minmax(160px,1fr)",
      cell: (r) => <span className="truncate">{r.name}</span>,
      edit: {
        get: (r) => r.name,
        save: (r, text) => {
          const name = text.trim();
          if (name) change(r.id, { name });
        },
      },
      total: () => <span className="truncate text-muted">Per unit{qty && total.perOrder > 0 ? ` (per-order charges over ${plural(qty, "unit")})` : ""}</span>,
    },
    {
      key: "amount",
      header: `Amount (${currency})`,
      width: "130px",
      align: "right",
      value: (r) => r.amount,
      cell: (r) =>
        r.amount == null ? <span className="text-faint">{amountRequired ? "0" : "Not known"}</span> : <span className="font-mono">{amountText(r.amount)}</span>,
      edit: {
        get: (r) => (r.amount == null ? "" : amountText(r.amount)),
        save: (r, text) => {
          const amount = parseAmount(text);
          if (amount === undefined) return;
          change(r.id, { amount: amount ?? (amountRequired ? 0 : null) });
        },
      },
      total: () => <span className="font-mono">{money(total.unit, currency)}</span>,
    },
    {
      key: "per",
      header: "Per",
      width: "120px",
      value: (r) => CHARGE_PER_LABELS[r.per],
      edit: { options: PER_OPTIONS, get: (r) => r.per, save: (r, value) => change(r.id, { per: value as ChargePer }) },
    },
    {
      key: "layer",
      header: "Counts as",
      width: "140px",
      value: (r) => CHARGE_LAYER_LABELS[r.layer],
      edit: { options: LAYER_OPTIONS, get: (r) => r.layer, save: (r, value) => change(r.id, { layer: value as ChargeLayer }) },
    },
    // The list's own order (what it's sorted by until someone sorts it another way).
    { key: "position", header: "#", optional: true, width: "48px", align: "right", value: (r) => position.get(r.id) ?? 0 },
    ...(editable
      ? [
          {
            key: "remove",
            header: "",
            width: "40px",
            value: () => null,
            cell: (r) => (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  save(rows.filter((x) => x.id !== r.id));
                }}
                title={`Remove ${r.name}`}
                className="rounded p-1 text-muted hover:bg-hover hover:text-text"
              >
                <X className="size-3.5" />
              </button>
            ),
          } satisfies Column<ChargeRow>,
        ]
      : []),
  ];
  return (
    <div className="flex flex-col gap-1">
      {rows.length > 0 && (
        <DataTable
          fit
          rows={rows}
          columns={columns}
          editing={editable}
          defaultSort={{ key: "position" }}
          rowNoun={["charge", "charges"]}
          exportName={exportName}
          viewsSlot={viewsSlot}
          pageFilters={false}
        />
      )}
      {rows.length > 0 && ((!qty && total.perOrder > 0) || total.missing.length > 0) && (
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-0.5 px-1 text-[12px] text-muted">
          {!qty && total.perOrder > 0 && (
            <span>
              Plus per order <span className="font-mono text-text">{money(total.perOrder, currency)}</span>
            </span>
          )}
          {total.missing.length > 0 && <span className="text-warn">No amount yet: {total.missing.join(", ")}</span>}
        </div>
      )}
      {editable && (
        <div className="flex flex-wrap items-center gap-1 pt-1">
          <span className="mr-1 flex items-center gap-1 text-[12px] text-muted">
            <Plus className="size-3.5" /> Add
          </span>
          {CHARGE_SUGGESTIONS.filter((s) => !used.has(s.name.toLowerCase())).map((s) => (
            <button
              key={s.name}
              type="button"
              onClick={() => add(s)}
              className="rounded-full border border-line px-2 py-0.5 text-[12px] hover:border-line-strong hover:bg-hover"
            >
              {s.name}
            </button>
          ))}
          <button
            type="button"
            onClick={() => add({ name: "Other", layer: "assembly", per: "unit" })}
            className="rounded-full border border-dashed border-line px-2 py-0.5 text-[12px] text-muted hover:border-line-strong hover:bg-hover"
          >
            Other…
          </button>
        </div>
      )}
    </div>
  );
}

/** A BOM family's charges (the BOM editor's Charges tab): the plan, in USD, for every revision. */
export function BomCharges({ bom }: { bom: Pick<Bom, "shopify_product_id" | "item_id" | "make_vendor_id"> }) {
  const all = useRows("build_charge");
  const vendors = useRows("vendor");
  const key = familyKey(bom);
  const mine = useMemo(() => all.filter((c) => familyKey(c) === key).sort((a, b) => a.position - b.position), [all, key]);
  const rows = useMemo(() => mine.map(toRow), [mine]);
  const maker = vendors.find((v) => v.id === bom.make_vendor_id);
  const editable = can("build_charge.set");
  const owner = bom.shopify_product_id ? { shopify_product_id: bom.shopify_product_id, item_id: null } : { item_id: bom.item_id, shopify_product_id: null };
  const save = (next: ChargeRow[]) =>
    mutateWithResult(
      "build_charge.set",
      {
        ...(bom.shopify_product_id ? { shopify_product_id: bom.shopify_product_id } : { item_id: bom.item_id }),
        charges: next.map((r) => ({ id: r.id, name: r.name, amount: r.amount, per: r.per, layer: r.layer })),
      },
      shown("build_charge", mine, next, owner),
    );
  // Table views --- the charges table's View and Columns menus, by the title
  const [slot, setSlot] = useState<HTMLDivElement | null>(null);
  return (
    <div className="max-w-[760px] p-4">
      <div className="mb-1 flex items-center gap-2 font-medium">
        {maker
          ? `What ${maker.short_name || maker.name} charges to build it`
          : bom.item_id
            ? "What it costs to build, charged by whoever builds what it's in"
            : "What its maker charges to build it"}
        <div ref={setSlot} className="ml-auto flex items-center gap-1 font-normal" />
      </div>
      <div className="mb-3 text-[12px] text-muted">
        Work and things that aren't parts with stock: soldering, testing, PCB fab, the components the maker sources, a stencil per order. They count in its cost
        like parts (each in its layer), and every revision shares them, so a new price needs no new revision. Each make PO copies them; change them there to
        that order's quote.
        {!maker && !bom.item_id && " Set Made by on the BOM to say who charges them."}
      </div>
      <ChargeTable rows={rows} currency="USD" editable={editable} onSave={save} exportName="Build charges" viewsSlot={slot} />
      {!rows.length && !editable && <div className="text-muted">No charges.</div>}
    </div>
  );
}

/** A make PO build's charges for this order (the PO's Make tab): they set the line's price. */
export function LineCharges({ po, line }: { po: Po; line: PoLine }) {
  const all = useRows("po_line_charge");
  const mine = useMemo(() => all.filter((c) => c.po_line_id === line.id).sort((a, b) => a.position - b.position), [all, line.id]);
  const rows = useMemo(() => mine.map(toRow), [mine]);
  const editable = linesEditable(po.status) && can("po_line.set_charges");
  const qty = Number(line.qty);
  const save = (next: ChargeRow[]) => {
    const charges = next.map((r) => ({ id: r.id, name: r.name, amount: r.amount ?? 0, per: r.per, layer: r.layer }));
    // The line's new price shows with them (an empty list leaves it).
    const price: Change[] = charges.length ? [{ t: "po_line", id: line.id, row: { ...line, unit_price: linePrice(charges, qty) } }] : [];
    return mutateWithResult("po_line.set_charges", { po_line_id: line.id, charges }, [
      ...shown("po_line_charge", mine, next, { po_line_id: line.id }),
      ...price,
    ]);
  };
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- by the title
  return (
    <div className="px-4 pt-2 pb-1">
      <div className="flex items-center gap-2 pb-1">
        <span className="text-[11px] text-faint">Charges for this order</span>
        <span className="text-[12px] text-muted">
          {rows.length
            ? `The price: ${money(line.unit_price, po.currency)} a unit, ${money(Number(line.unit_price ?? 0) * qty, po.currency)} for ${plural(qty, "unit")}`
            : `${line.unit_price == null ? "No price yet" : `${money(line.unit_price, po.currency)} a unit`}, not broken down`}
        </span>
        {!rows.length && editable && line.unit_price != null && (
          <button
            type="button"
            className="text-[12px] text-accent-text hover:underline"
            onClick={() => save([{ id: crypto.randomUUID(), name: "Build", amount: Number(line.unit_price), per: "unit", layer: "assembly" }])}
          >
            Break it down
          </button>
        )}
        <div ref={setSlot} className="ml-auto flex items-center gap-1" />
      </div>
      {(rows.length > 0 || (editable && line.unit_price == null)) && (
        <ChargeTable
          rows={rows}
          currency={po.currency}
          editable={editable}
          amountRequired
          qty={qty}
          onSave={save}
          exportName="Order charges"
          viewsSlot={slot}
        />
      )}
    </div>
  );
}
